import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { useHubToast } from "@/components/ui/HubToast";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { EngineeringChangeMarkdown } from "@/pages/documents/EngineeringChangeMarkdown";

export type PersonJobDescription = {
  id: number;
  jobTitle: string;
  body: string;
  effectiveOn: string;
  endedOn: string | null;
};

type PersonJobDescriptionsProps = {
  staffId: number;
  descriptions: PersonJobDescription[];
  writesAllowed: boolean;
  readOnlyHint: string;
};

export function PersonJobDescriptions({
  staffId,
  descriptions,
  writesAllowed,
  readOnlyHint,
}: PersonJobDescriptionsProps) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [jobTitle, setJobTitle] = useState("");
  const [body, setBody] = useState("");
  const [effectiveOn, setEffectiveOn] = useState("");
  const [endedOn, setEndedOn] = useState("");

  const addMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/people/staff/${staffId}/job-descriptions`, {
        method: "POST",
        body: {
          jobTitle,
          body,
          effectiveOn,
          endedOn: endedOn.trim() === "" ? null : endedOn,
        },
      }),
    onSuccess: async () => {
      setJobTitle("");
      setBody("");
      setEffectiveOn("");
      setEndedOn("");
      await queryClient.invalidateQueries({ queryKey: ["people", "staff", staffId] });
      await queryClient.invalidateQueries({ queryKey: ["people", "directory"] });
      showToast({ message: "Job description saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  return (
    <HubSettingsSection
      title="Job descriptions"
      hoverHint="Each title and description applies from an effective date through an end date. Leave the end date empty while it is still current. A new description that starts later closes the open one the day before. The text is Markdown."
    >
      <div className="space-y-4">
        {descriptions.length === 0 ? (
          <p className="text-sm text-slate-600">No job description yet.</p>
        ) : (
          descriptions.map((description) => (
            <article key={description.id} className="rounded-md border border-slate-200 p-3">
              <p className="text-sm font-medium text-slate-900">
                {description.jobTitle}
                <span className="ml-2 font-normal text-slate-600">
                  {description.effectiveOn}
                  {" through "}
                  {description.endedOn ?? "open"}
                </span>
              </p>
              {description.body.trim() !== "" ? (
                <EngineeringChangeMarkdown>{description.body}</EngineeringChangeMarkdown>
              ) : null}
            </article>
          ))
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Label htmlFor="people-description-title">Title</Label>
            <Input
              id="people-description-title"
              className="mt-1"
              value={jobTitle}
              disabled={!writesAllowed}
              onChange={(event) => setJobTitle(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="people-description-effective">Effective</Label>
            <Input
              id="people-description-effective"
              type="date"
              className="mt-1"
              value={effectiveOn}
              disabled={!writesAllowed}
              onChange={(event) => setEffectiveOn(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="people-description-ended">Ended</Label>
            <Input
              id="people-description-ended"
              type="date"
              className="mt-1"
              value={endedOn}
              disabled={!writesAllowed}
              onChange={(event) => setEndedOn(event.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="people-description-body">Description (Markdown)</Label>
            <textarea
              id="people-description-body"
              className="mt-1 min-h-40 w-full rounded-md border border-slate-300 px-2 py-2 font-mono text-sm"
              value={body}
              disabled={!writesAllowed}
              onChange={(event) => setBody(event.target.value)}
            />
            {body.trim() !== "" ? <EngineeringChangeMarkdown>{body}</EngineeringChangeMarkdown> : null}
          </div>
        </div>
        <ReadOnlyHoverLayer active={!writesAllowed} hoverHint={readOnlyHint}>
          <Button
            type="button"
            disabled={!writesAllowed || jobTitle.trim() === "" || effectiveOn === "" || addMutation.isPending}
            onClick={() => addMutation.mutate()}
          >
            Add job description
          </Button>
        </ReadOnlyHoverLayer>
      </div>
    </HubSettingsSection>
  );
}
